import React from "react";
import clsx from "clsx";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import { racehorse360 } from "@tsg/1st-grpc-web-rh360";
import Dialog from "components/Dialog";
import Link from "components/Link";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import routes from "common/routes";
import { AlertEnum } from "utils/horse";
import useStyles from "./styles";

export interface Props {
  open: boolean;
  horse: racehorse360.IHorse;
  daysOff: number | string;
  onClose: () => void;
  status: AlertEnum;
  limit: number;
  nextWorkout?: racehorse360.IWorkoutRequest | undefined;
  showViewHorseButton?: boolean;
}

type Content = {
  title?: string;
  description: React.ReactNode;
};

const capitalise = (string: string) => {
  return string
    .toLowerCase()
    .split(" ")
    .map(str => {
      return str.charAt(0).toUpperCase() + str.slice(1);
    })
    .join(" ");
};

const getPopupContent = (
  status,
  horse,
  daysOff,
  limit,
  nextWorkout
): Content => {
  switch (status) {
    case AlertEnum.high: //red
      return {
        title: "Workout Required",
        description: (
          <>
            {nextWorkout && nextWorkout.date ? (
              <>
                <b>{horse.name}</b> has not raced for more than {limit} days and
                has a workout scheduled for{" "}
                {format(parseISO(nextWorkout.date), "M/d/yy")} at{" "}
                {capitalise(nextWorkout.facility.name)}.
              </>
            ) : (
              <>
                <b>{horse.name}</b> has not raced for more than {limit} days and
                a workout is required.
              </>
            )}
          </>
        )
      };
    case AlertEnum.moderate: //orange
      return {
        title: "Workout Recommended",
        description: (
          <>
            {nextWorkout && nextWorkout.date ? (
              <>
                <b>{horse.name}</b> has not raced for {daysOff} days and has a
                workout scheduled for{" "}
                {format(parseISO(nextWorkout.date), "M/d/yy")} at{" "}
                {capitalise(nextWorkout.facility.name)}.
              </>
            ) : (
              <>
                <b>{horse.name}</b> has not raced for {daysOff} days and will
                require a workout soon.
              </>
            )}
          </>
        )
      };
    case AlertEnum.middle: //yellow
      return {
        title: "Workout Optional",
        description: (
          <>
            {nextWorkout && nextWorkout.date ? (
              <>
                <b>{horse.name}</b> has not raced for {daysOff} days and has a
                workout scheduled for{" "}
                {format(parseISO(nextWorkout.date), "M/d/yy")} at{" "}
                {capitalise(nextWorkout.facility.name)}.
              </>
            ) : (
              <>
                <b>{horse.name}</b> has not raced for {daysOff} days and will
                require a workout soon.
              </>
            )}
          </>
        )
      };
    case AlertEnum.non: //green
      return {
        title: "Workout Optional",
        description: (
          <>
            {nextWorkout && nextWorkout.date ? (
              <>
                <b>{horse.name}</b> has not raced for {daysOff} days and has a
                workout scheduled for{" "}
                {format(parseISO(nextWorkout.date), "M/d/yy")} at{" "}
                {capitalise(nextWorkout.facility.name)}.
              </>
            ) : (
              <>
                <b>{horse.name}</b> has not raced for {daysOff} days.
              </>
            )}
          </>
        )
      };
    default:
      //grey
      return {
        description: (
          <>
            <b>{horse.name}</b> has been inactive for an unknown period of time.
          </>
        )
      };
  }
};

const WorkoutRequiredDialogDaysOff = (props: Props) => {
  const {
    open,
    horse,
    onClose,
    daysOff,
    status,
    limit,
    nextWorkout,
    showViewHorseButton = true
  } = props;
  const classes = useStyles();
  const { currentUser } = useLoggedInUser();
  const popupContent = getPopupContent(
    status,
    horse,
    daysOff,
    limit,
    nextWorkout
  );

  return (
    <>
      <Dialog
        title={popupContent.title}
        hideHeader={status === AlertEnum.na}
        hideXButton
        alert
        onClose={onClose}
        aria-labelledby="customized-dialog-title"
        className={classes.horseAlertDialog}
        titleClassName={classes[status]}
        isOpen={open}
      >
        <Box>
          <Typography className={classes.horseAlertDialogMessage}>
            {popupContent.description}
          </Typography>
        </Box>
        <DialogActions className={classes.dialogActions}>
          <Box className={classes.leftActions}>
            <Button
              onClick={onClose}
              className={clsx(
                classes.dialogButtonClose,
                classes.dialogActionButton
              )}
            >
              Close
            </Button>
          </Box>
          <Box className={classes.rightActions}>
            {showViewHorseButton && (
              <Link
                to={routes.horseDetails.path.replace(
                  ":horseId",
                  String(horse.id)
                )}
                className={clsx(
                  classes.dialogButtonHorseDetails,
                  classes.dialogActionButton
                )}
                button
              >
                VIEW HORSE
              </Link>
            )}
            {(currentUser.isTrainer || currentUser.isRacingOfficial) && (
              <Link
                to={routes.horseWorkouts.path.replace(
                  ":horseId",
                  String(horse.id)
                )}
                className={clsx(
                  classes.dialogButtonSchedule,
                  classes.dialogActionButton
                )}
                button
              >
                {status === AlertEnum.na
                  ? "SCHEDULE WORKOUT"
                  : nextWorkout
                  ? "VIEW WORKOUT"
                  : "SCHEDULE NOW"}
              </Link>
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WorkoutRequiredDialogDaysOff;
